import { useState, useEffect, useCallback, useMemo } from "react"
import { listIntegrationConnectionsApi } from "@/api/integrations"
import type { NylasConnection } from "@equaliq/api-model-v2"

interface UseIntegrationConnectionsOptions {
  /** When false, skips the API call (e.g. in demo mode). Defaults to true. */
  enabled?: boolean
}

/**
 * Hook to manage Nylas integration connections.
 * Loads connections and provides utilities for mapping connections to integrations.
 */
export function useIntegrationConnections(
  options: UseIntegrationConnectionsOptions = {}
) {
  const { enabled = true } = options
  const [connections, setConnections] = useState<NylasConnection[]>([])
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<Error | null>(null)

  const loadConnections = useCallback(async () => {
    if (!enabled) return
    try {
      setLoading(true)
      setError(null)
      const result = await listIntegrationConnectionsApi()
      setConnections(result.connections || [])
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load connections"))
      console.error("Failed to load connections:", err)
    } finally {
      setLoading(false)
    }
  }, [enabled])

  useEffect(() => {
    if (enabled) {
      loadConnections()
    }
  }, [enabled, loadConnections])

  // Map connections to integration IDs
  const connectedIntegrationIds = useMemo(() => {
    const ids = new Set<string>()
    connections.forEach((conn) => {
      const provider = conn.provider?.toLowerCase() || ""
      const scopes = conn.scopes || []

      // Google integrations
      if (provider === "google") {
        if (scopes.some((s) => s.includes("gmail") || s.includes("mail"))) {
          ids.add("gmail")
        }
        if (scopes.some((s) => s.includes("calendar"))) {
          ids.add("google-calendar")
        }
      }

      // Microsoft/Outlook integrations
      if (provider === "microsoft" || provider === "outlook") {
        if (scopes.some((s) => s.includes("mail"))) {
          ids.add("outlook-email")
        }
        if (scopes.some((s) => s.includes("calendar"))) {
          ids.add("outlook-calendar")
        }
      }
    })
    return ids
  }, [connections])

  // Map integration IDs to connections (first connection only)
  const integrationConnectionMap = useMemo(() => {
    const map = new Map<string, NylasConnection>()
    connections.forEach((conn) => {
      const provider = conn.provider?.toLowerCase() || ""
      const scopes = conn.scopes || []

      const integrationIds: string[] = []

      // Google integrations
      if (provider === "google") {
        if (scopes.some((s) => s.includes("gmail") || s.includes("mail"))) {
          integrationIds.push("gmail")
        }
        if (scopes.some((s) => s.includes("calendar"))) {
          integrationIds.push("google-calendar")
        }
      }

      // Microsoft/Outlook integrations
      if (provider === "microsoft" || provider === "outlook") {
        if (scopes.some((s) => s.includes("mail"))) {
          integrationIds.push("outlook-email")
        }
        if (scopes.some((s) => s.includes("calendar"))) {
          integrationIds.push("outlook-calendar")
        }
      }

      integrationIds.forEach((id) => {
        if (!map.has(id)) {
          map.set(id, conn)
        }
      })
    })
    return map
  }, [connections])

  // Get all connections for a specific integration
  const getConnectionsForIntegration = useCallback((integrationId: string): NylasConnection[] => {
    return connections.filter((conn) => {
      const provider = conn.provider?.toLowerCase() || ""
      const scopes = conn.scopes || []

      // Google integrations
      if (integrationId === "gmail") {
        return provider === "google" && scopes.some((s) => s.includes("gmail") || s.includes("mail"))
      }
      if (integrationId === "google-calendar") {
        return provider === "google" && scopes.some((s) => s.includes("calendar"))
      }

      // Microsoft/Outlook integrations
      if (integrationId === "outlook-email") {
        return (provider === "microsoft" || provider === "outlook") && scopes.some((s) => s.includes("mail"))
      }
      if (integrationId === "outlook-calendar") {
        return (provider === "microsoft" || provider === "outlook") && scopes.some((s) => s.includes("calendar"))
      }

      return false
    })
  }, [connections])

  return {
    connections,
    loading,
    error,
    connectedIntegrationIds,
    integrationConnectionMap,
    getConnectionsForIntegration,
    refetch: loadConnections,
  }
}
